import { createContext, FC, ReactNode, useContext, useMemo } from 'react'

/**
 * Scope identifying the currently-active unit component inside a tabbed step
 * component. Mounted by `UnitComponentTabs` around the active tab's React
 * subtree so scoped hooks (`useScopedFieldAccess`, `useFfsTicketContext`,
 * `useWorkflowFieldValues`) can pick up the active componentId without each
 * caller threading it through props.
 *
 * `inspectionId` is the most-recent inspection for this component, resolved
 * at render time by `pickMostRecentInspection` (see
 * `selectComponentInspection.ts`) rather than persisted on the ticket.
 * Exposed here so FFS hooks like `useFfsTicketContext` can read it the same
 * way they read `componentId`.
 */
export interface ComponentScope {
  componentId: string
  inspectionId: string | undefined
}

const ComponentScopeContext = createContext<ComponentScope | null>(null)

export const ComponentScopeProvider: FC<{
  children: ReactNode
  componentId: string
  inspectionId: string | undefined
}> = ({ children, componentId, inspectionId }) => {
  const value = useMemo<ComponentScope>(
    () => ({ componentId, inspectionId }),
    [componentId, inspectionId]
  )
  return (
    <ComponentScopeContext.Provider value={value}>
      {children}
    </ComponentScopeContext.Provider>
  )
}

/**
 * Reads the surrounding component scope, or `null` when no scope is mounted
 * (e.g. steps that don't use tabs). Hook-rules-compliant: always called
 * unconditionally; consumers branch on the returned value, not on whether to
 * call the hook.
 */
export const useComponentScope = (): ComponentScope | null =>
  useContext(ComponentScopeContext)
